import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';

import { styled } from '@mui/material/styles';

import { ChevronDown, SettingIcon } from 'icons';
import { CustomRemoteLevel } from './CustomRemoteLevel';

import type { RemoteLevel } from 'types';

type RemoteLevelTileProps = {
  level: RemoteLevel;
  isSelected: boolean;
  isOpened: boolean;
  changeSelectedRemoteLevel: (remoteLevel: RemoteLevel) => void;
  toggleRemoteLevel: (remoteLevel: RemoteLevel) => void;
};

const StyledAccordionSummary = styled(AccordionSummary, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{
  isSelected: boolean;
}>(({ isSelected, theme }) => ({
  backgroundColor: theme.palette.remoteLevelCard.bg,
  borderRadius: '6px',
  border: '1px solid transparent',
  transition: '0.2s',
  marginBottom: '4px',

  '&:hover': {
    backgroundColor: isSelected ? 'none' : theme.palette.grey[100],
    borderColor: theme.palette.primary.main,
  },

  ...(isSelected && {
    backgroundColor: 'none',
    borderColor: theme.palette.primary.main,
  }),

  '&.MuiAccordionSummary-root': {
    padding: 0,
    paddingRight: '8px',

    '&.Mui-expanded': {
      marginBottom: '4px',
    },
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    padding: '0 18px 0 16px',
    minHeight: '82px',
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const ACCORDION_TRANSITION_MS = 300;

export const RemoteLevelTile = ({
  level,
  isSelected,
  isOpened,
  changeSelectedRemoteLevel,
  toggleRemoteLevel,
}: RemoteLevelTileProps) => {
  const accordionRef = React.useRef<HTMLDivElement>(null);

  const { isCustom, level: levelNumber, info, name, overlapLabel } = level;

  React.useEffect(() => {
    isOpened &&
      setTimeout(() => {
        accordionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, ACCORDION_TRANSITION_MS);
  }, [isOpened]);

  return (
    <Accordion
      disableGutters
      ref={accordionRef}
      expanded={isOpened}
      TransitionProps={{
        timeout: ACCORDION_TRANSITION_MS,
      }}
      sx={{
        scrollMarginTop: '92px',
        '& .MuiCollapse-entered': {
          marginBottom: '4px',
        },
      }}
    >
      <StyledAccordionSummary
        isSelected={isSelected}
        onClick={() =>
          isCustom ? toggleRemoteLevel(level) : changeSelectedRemoteLevel(level)
        }
        expandIcon={
          <IconButton
            onClick={(e) => {
              if (isCustom) return;

              e.stopPropagation();
              toggleRemoteLevel(level);
            }}
          >
            {isCustom ? <SettingIcon /> : <ChevronDown />}
          </IconButton>
        }
      >
        <Box display="flex" alignItems="center">
          <Box
            sx={{
              backgroundColor: 'secondary.light',
              minHeight: 40,
              minWidth: 40,
            }}
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mr={4}
          >
            <Typography
              variant="textMedium"
              color="text.regular"
              fontWeight={700}
              sx={{ userSelect: 'none' }}
            >
              {levelNumber}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography
              variant="textMedium"
              color="text.regular"
              fontWeight={700}
            >
              {name}
            </Typography>
            {overlapLabel && (
              <Typography variant="textSmall" color="text.regular">
                {overlapLabel}
              </Typography>
            )}
          </Box>
        </Box>
      </StyledAccordionSummary>

      <AccordionDetails
        sx={{
          border: ({ palette }) => `1px solid ${palette.grey[200]}`,
          borderRadius: '6px',
          padding: '24px 16px',
        }}
      >
        {isCustom ? (
          <CustomRemoteLevel
            changeSelectedRemoteLevel={changeSelectedRemoteLevel}
          />
        ) : (
          <>
            {info.map((row) => (
              <Box key={row} display="flex" color="text.regular">
                <Box fontSize="20px" lineHeight="20px" mr={4}>
                  &bull;
                </Box>
                <Typography variant="textSmall">{row}</Typography>
              </Box>
            ))}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
